import { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import axiosInstance from "../../axiosInstance";
import { Formik, Form } from "formik";
import InputField from "./InputFieldWithError";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";
import "./UserManagement.scss";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css"; // ✅ Make sure CSS is imported
import { toast } from "react-toastify";
import GeneralTable from "../../components/Table/GeneralTable";
import IconButton from "../../components/IconButton/IconButton";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import FileInput from "../../components/InputComponent/FileInput";
import { Info } from "lucide-react";

const validationSchema = Yup.object().shape({
  employeeUserId: Yup.string().optional(),
  firstName: Yup.string()
    .required("First Name is required")
    .min(2, "First Name must be at least 2 characters long"),
  lastName: Yup.string()
    .required("Last Name is required")
    .min(1, "Last Name must be at least 1 characters long"),
  dob: Yup.date()
    .required("Date of Birth is required")
    .max(new Date(), "Date of Birth cannot be in the future"),
  password: Yup.string().when("$isEditMode", {
    is: false, // Only required when not in edit mode
    then: (schema) =>
      schema
        .required("Password is required")
        .min(8, "Password must be at least 8 characters long"),
    otherwise: (schema) => schema.optional(),
  }),
  pfNumber: Yup.string()
    .matches(/^[A-Z0-9]{6,20}$/, "PF number must be 6–20 letters/numbers")
    .optional(),
  aadharNumber: Yup.string()
    .optional()
    .matches(/^[0-9]{12}$/, "Aadhar number must be 12 digits"),
  dateOfJoin: Yup.date()
    .required("Date of Joining is required")
    .max(new Date(), "Date of Joining cannot be in the future"),
  officialEmail: Yup.string()
    .email("Invalid email format")
    .required("Official Email is required"),
  personalEmail: Yup.string()
    .email("Invalid email format")
    .required("Personal Email is required"),
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
  maritalStatus: Yup.string().required("Marital Status is required"),
  gender: Yup.string().required("Gender is required"),
  bloodGroup: Yup.string().required("Blood Group is required"),
  emergencyContactName: Yup.string().required("Emergency Name required"),
  emergencyContactNo: Yup.string()
    .required("Emergency Number required")
    .matches(/^[0-9]{10}$/, "Emergency Number must be 10 digits"),
  roleId: Yup.string().required("Role is required"),
  branchId: Yup.string().required("Branch is required"),
  managerId: Yup.string().nullable(),
  accountantId: Yup.string().nullable(),
  departmentIds: Yup.array()
    .min(1, "At least one be selected")
    .required("Departments are required"),
  authorities: Yup.array()
    .min(1, "At least one be selected")
    .required("Authorities are required"),
  officeTimingId: Yup.string().required("Office Timing is required"),
  workingDayId: Yup.string().required("Working Day is required"),
  profilePic: Yup.mixed()
    .nullable()
    .test("fileSize", "File size must be 500KB or less", (value) => {
      return !value || (value && value.size <= 500 * 1024);
    })
    .test("fileType", "Only JPG, JPEG, and PNG files are allowed", (value) => {
      return (
        !value ||
        (value && ["image/jpg", "image/jpeg", "image/png"].includes(value.type))
      );
    }),
  skillLevel: Yup.string().required("Skill level is required"),
  probationPeriod: Yup.string().required("Probation period is required"),
  employementStatus: Yup.string().required("Employment status is required"),
  employmentLevel: Yup.string().required("Employment level is required"),
  employmentType: Yup.string().required("Employment type is required"),
  bankName: Yup.string().optional(),
  accountHolderName: Yup.string().optional(),
  accountNumber: Yup.string().when("bankName", {
    is: (value) => value && value.length > 0,
    then: (schema) =>
      schema.required("Account number is required when bank name is provided"),
    otherwise: (schema) => schema.optional(),
  }),
  ifscCode: Yup.string()
    .optional()
    .matches(/^[A-Z]{4}[0-9]{7}$/, "Invalid IFSC code format"),
  branchName: Yup.string().optional(),
  branchAddress: Yup.string().optional(),
  bankAccountType: Yup.string().optional(),
  upiId: Yup.string().optional(),
  micrCode: Yup.string().optional(),
  panNumber: Yup.string()
    .optional()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
  uanNumber: Yup.string()
    .optional()
    .matches(/^[0-9]{12}$/, "UAN must be 12 digits"),
  insuranceNo: Yup.string().optional(),
  hrId: Yup.string().nullable(),
});

const useScrollToError = () => {
  const { errors, isSubmitting, isValidating } = useFormikContext();

  useEffect(() => {
    if (isSubmitting && !isValidating) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        // Try multiple selectors to find the error element
        const selectors = [
          `[name="${firstErrorField}"]`,
          `#${firstErrorField}`,
          `[data-field="${firstErrorField}"]`,
          `.form-field:has([name="${firstErrorField}"])`,
          `.form-field:has(#${firstErrorField})`,
        ];

        let errorElement = null;

        for (const selector of selectors) {
          try {
            errorElement = document.querySelector(selector);
            if (errorElement) break;
          } catch (e) {
            // Skip invalid selectors (like :has() in some browsers)
            continue;
          }
        }

        // If still not found, look for parent form-field containing the error
        if (!errorElement) {
          const errorMessages = document.querySelectorAll(".error-message");
          for (const errorMsg of errorMessages) {
            const formField = errorMsg.closest(".form-field");
            if (formField) {
              const fieldElement = formField.querySelector("[name], [id]");
              if (
                fieldElement &&
                (fieldElement.name === firstErrorField ||
                  fieldElement.id === firstErrorField)
              ) {
                errorElement = fieldElement;
                break;
              }
            }
          }
        }

        if (errorElement) {
          // Scroll to the error element with smooth behavior
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          // Focus on the element after scrolling (only if it's focusable)
          setTimeout(() => {
            if (
              errorElement.tabIndex >= 0 ||
              ["input", "select", "textarea", "button"].includes(
                errorElement.tagName.toLowerCase(),
              )
            ) {
              errorElement.focus();
            }
          }, 500);
        }
      }
    }
  }, [errors, isSubmitting, isValidating]);
};

// Component to include in your form (unchanged)
const ScrollToError = () => {
  useScrollToError();
  return null;
};

const maritalStatusOptions = [
  { value: "SINGLE", label: "Single" },
  { value: "MARRIED", label: "Married" },
  { value: "DIVORCED", label: "Divorced" },
  { value: "WIDOWED", label: "Widowed" },
  { value: "SEPARATED", label: "Separated" },
];

const employmentTypeOptions = [
  { value: "PERMANENT", label: "Permanent" },
  { value: "INTERN", label: "Intern" },
  { value: "CONTRACT", label: "Contract" },
  { value: "PART_TIME", label: "Part Time" },
];

const employmentLevelOptions = [
  { value: "ENTRY_LEVEL", label: "Entry Level" },
  { value: "MID_LEVEL", label: "Mid Level" },
  { value: "TOP_MANAGEMENT", label: "Top Management" },
];

const employmentStatusOptions = [
  { value: "PROBATION", label: "Probation" },
  { value: "CONFIRMED", label: "Confirmed" }, // Changed from "CONFIRM" to "CONFIRMED"
  { value: "ACTIVE", label: "Active" },
  { value: "TERMINATED", label: "Terminated" },
  { value: "RESIGNED", label: "Resigned" },
];

const probationPeriodOptions = [
  { value: "NONE", label: "None" },
  { value: "ONE_MONTH", label: "1 Month" },
  { value: "TWO_MONTHS", label: "2 Months" },
  { value: "THREE_MONTHS", label: "3 Months" },
  { value: "SIX_MONTHS", label: "6 Months" },
  { value: "TWELVE_MONTHS", label: "12 Months" },
];

const skillLevelOptions = [
  { value: "NOVICE", label: "Novice" },
  { value: "ADVANCED_BEGINNER", label: "Advanced Beginner" },
  { value: "COMPETENT", label: "Competent" },
  { value: "PROFICIENT", label: "Proficient" },
  { value: "EXPERT", label: "Expert" },
];

const bankAccountTypeOptions = [
  { value: "SAVINGS", label: "Savings" },
  { value: "CURRENT", label: "Current" },
  { value: "SALARY", label: "Salary" },
  { value: "NRE", label: "NRE" },
  { value: "NRO", label: "NRO" },
];

const genderOptions = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
  { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
];

const bloodGroupOptions = [
  { value: "A_POSITIVE", label: "A+" },
  { value: "A_NEGATIVE", label: "A-" },
  { value: "B_POSITIVE", label: "B+" },
  { value: "B_NEGATIVE", label: "B-" },
  { value: "AB_POSITIVE", label: "AB+" },
  { value: "AB_NEGATIVE", label: "AB-" },
  { value: "O_POSITIVE", label: "O+" },
  { value: "O_NEGATIVE", label: "O-" },
];

const UserManagement = () => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  //eslint-disable-next-line no-unused-vars
  const [filteredOfficeTimings, setFilteredOfficeTimings] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  //eslint-disable-next-line no-unused-vars
  const [employees, setEmployees] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState("");
  const [workingDays, setWorkingDays] = useState([]);
  const [filteredWorkingDays, setFilteredWorkingDays] = useState([]);
  const [isFormReady, setIsFormReady] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [templateFile, setTemplateFile] = useState(null);
  //eslint-disable-next-line no-unused-vars
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accountants, setAccountants] = useState([]);
  const [hrPersons, setHrPersons] = useState([]);

  const EnhancedMultiSelect = ({
    options,
    value,
    onChange,
    placeholder,
    label,
    showSelectAll = true,
    dependencyMessage = null,
    name, // Add name prop
    error, // Add error prop
    touched, // Add touched prop
  }) => {
    const handleSelectAll = () => {
      if (value.length === options.length) {
        onChange([]);
      } else {
        onChange(options);
      }
    };

    const isAllSelected = value.length === options.length && options.length > 0;

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          {label && (
            <label className="text-sm font-medium text-gray-700">{label}</label>
          )}
          {showSelectAll && options.length > 0 && (
            <button
              type="button"
              className="text-blue-500 text-sm font-medium hover:underline focus:outline-none"
              onClick={handleSelectAll}
            >
              {isAllSelected ? "Deselect All" : "Select All"}
            </button>
          )}
        </div>

        <div
          className="rounded-xl border border-gray-700 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all"
          id={name}
          tabIndex={-1}
        >
          <Select
            options={options}
            value={value}
            onChange={onChange}
            isMulti
            placeholder={placeholder}
            classNamePrefix="custom-select"
            isDisabled={options.length === 0}
            styles={{
              control: (provided, state) => ({
                ...provided,
                border: "none",
                boxShadow: "none",
                padding: "6px",
                minHeight: "44px",
                backgroundColor: "transparent",
              }),
              placeholder: (provided) => ({
                ...provided,
                color: "#6b7280",
                fontSize: "0.95rem",
              }),
              multiValue: (provided) => ({
                ...provided,
                backgroundColor: "#f3f4f6",
                borderRadius: "6px",
                padding: "2px 6px",
              }),
              multiValueLabel: (provided) => ({
                ...provided,
                color: "#374151",
                fontWeight: 500,
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: "#6b7280",
                ":hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#111827",
                },
              }),
            }}
          />
        </div>

        <div className="mt-2 text-sm text-blue-500">
          {value.length} of {options.length} selected
        </div>

        {dependencyMessage && (
          <div className="mt-2 flex items-center text-xs text-blue-500">
            <Info className="w-4 h-4 mr-1 text-gray-400" />
            <span>{dependencyMessage}</span>
          </div>
        )}

        {touched && error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const ButtonGroup = ({ options, value, onChange, name, error, touched }) => {
    return (
      <div className="form-row ml-1">
        <div className="button-group" id={name} tabIndex={-1}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`option-button ${
                value === option.value ? "selected" : ""
              }`}
              onClick={() => {
                onChange(option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {touched && error && <div className="error-message">{error}</div>}
      </div>
    );
  };

  const generatePassword = (firstName = "", lastName = "", dob = "") => {
    if (!dob || !dob.includes("-")) {
      console.error("Invalid DOB format. Expected YYYY-MM-DD.");
      return "";
    }

    const firstPart = firstName.slice(0, 3).toLowerCase() || "usr";
    const [, month, day] = dob.split("-"); // Parse YYYY-MM-DD format, ignore year
    const datepart = `${day || "01"}${month || "01"}`; // DDMM format
    const password = `${firstPart}${datepart}`;

    return password;
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/All`,
      );
      setTableData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleEditUser = async (user) => {
    setSelectedUser(user);
    setIsFormReady(false);

    if (user.branch?.branchId) {
      const selectedBranch = branches.find(
        (branch) => branch.branchId === user.branch.branchId,
      );

      if (selectedBranch) {
        // Update states
        setDepartments(selectedBranch.departments || []);
        setFilteredOfficeTimings(selectedBranch.officeTimings || []);

        const branchWorkingDays = workingDays.filter((workingDay) =>
          workingDay.branches?.some(
            (branch) => branch.branchId === user.branch.branchId,
          ),
        );
        setFilteredWorkingDays(branchWorkingDays);

        // ✅ Fix: Use setTimeout to ensure state updates complete
        setTimeout(() => {
          setIsFormReady(true);
          setShowAddUserModal(true);
        }, 10); // Increased from 0 to 10ms
      }
    } else {
      setIsFormReady(true);
      setShowAddUserModal(true);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axiosInstance.delete(
          `${process.env.REACT_APP_API_BASE_URL}/employees/${userId}`,
        );
        toast.success("User deleted successfully!");
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  // ✅ Date Formatter
  const getFormattedDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatAccountType = (type) =>
    ({
      SAVINGS: "Savings",
      CURRENT: "Current",
      SALARY: "Salary",
    })[type] || "N/A";

  const horizontalTableColumns = [
    { header: "Employee", accessor: "employeeProfile", sortable: false },
    { header: "First Name", accessor: "firstName", sortable: true },
    { header: "Last Name", accessor: "lastName", sortable: true },
    { header: "Mobile", accessor: "mobileNo", sortable: true },
    { header: "Company Email", accessor: "companyEmail", sortable: true },
    { header: "Personal Email", accessor: "email", sortable: true },
    { header: "Gender", accessor: "gender", sortable: true },
    { header: "Blood Group", accessor: "bloodGroup", sortable: true },
    { header: "Marital Status", accessor: "martialStatus", sortable: true },
    { header: "DOB", accessor: "dob", sortable: true },
    { header: "DOJ", accessor: "doj", sortable: true },
    { header: "Branch", accessor: "branchName", sortable: true },
    { header: "Role", accessor: "roleName", sortable: true },
    { header: "Manager", accessor: "managerName", sortable: true },
    { header: "HR Name", accessor: "hrName", sortable: true },
    { header: "Accountant Name", accessor: "accountantName", sortable: true },
    { header: "Department", accessor: "departmentNames", sortable: true },
    {
      header: "Emergency Contact",
      accessor: "emergencyContactName",
      sortable: true,
    },
    {
      header: "Emergency Number",
      accessor: "emergencyContactNo",
      sortable: true,
    },
    { header: "Employment Type", accessor: "employmentType", sortable: true },
    { header: "Skill Level", accessor: "skillLevel", sortable: true },
    { header: "Probation Period", accessor: "probationPeriod", sortable: true },
    {
      header: "Employment Status",
      accessor: "employmentStatus",
      sortable: true,
    },
    { header: "Employment Level", accessor: "employmentLevel", sortable: true },
    { header: "Aadhar Number", accessor: "aadharNumber", sortable: true },
    { header: "Insurance No", accessor: "insuranceNo", sortable: true },
    { header: "PAN Number", accessor: "panNumber", sortable: true },
    { header: "PF Number", accessor: "pfNumber", sortable: true },
    { header: "UAN Number", accessor: "uanNumber", sortable: true },
    { header: "Bank Name", accessor: "bankName", sortable: true },
    { header: "Account Holder", accessor: "accountHolderName", sortable: true },
    { header: "Account Number", accessor: "accountNumber", sortable: true },
    { header: "IFSC Code", accessor: "ifscCode", sortable: true },
    { header: "Account Type", accessor: "bankAccountType", sortable: true },
    { header: "UPI ID", accessor: "upiId", sortable: true },
    { header: "Access", accessor: "authorities", sortable: false },
    { header: "Actions", accessor: "actions", sortable: false },
  ];

  // ✅ Transform Data
  const horizontalTransformedData = tableData.map((employee) => {
    const fullName = `${employee.firstName || ""} ${
      employee.lastName || ""
    }`.trim();

    return {
      ...employee,
      firstName: employee.firstName || "N/A",
      lastName: employee.lastName || "N/A",

      // Profile Info
      employeeProfile: (
        <div className="employee-profile-cell">
          <div className="profile-image-container">
            {employee.profileImage ? (
              <img
                src={`data:${employee.profileImageType};base64,${employee.profileImage}`}
                alt={`${fullName} profile`}
                className="profile-image"
              />
            ) : (
              <div className="profile-placeholder">
                {(employee.firstName?.charAt(0) || "") +
                  (employee.lastName?.charAt(0) || "") || "?"}
              </div>
            )}
          </div>
          <div className="profile-info">
            <div className="employee-name">{fullName || "N/A"}</div>
            <div className="employee-id">
              ID: {employee.userEmployeeId || "N/A"}
            </div>
          </div>
        </div>
      ),

      // Basic Info
      mobileNo: employee.mobileNo || "N/A",
      companyEmail: employee.companyEmail || "N/A",
      email: employee.email || "N/A",
      gender: employee.gender || "N/A",
      bloodGroup: employee.bloodGroup || "N/A",
      martialStatus: employee.martialStatus || "N/A",
      dob: getFormattedDate(employee.dob),
      doj: getFormattedDate(employee.doj),
      branchName: employee.branch?.branchName || "N/A",
      roleName: employee.role?.roleName || "N/A",

      // Managers & HR
      managerName:
        employee.managerId === employee.empId
          ? "Self"
          : employee.managerName || "N/A",
      hrName: employee.hrName || "N/A",
      accountantName: employee.accountantName || "N/A",

      departmentNames:
        employee.departments?.map((dept) => dept.departmentName).join(", ") ||
        "N/A",
      emergencyContactName: employee.emergencyContactName || "N/A",
      emergencyContactNo: employee.emergencyContactNo || "N/A",

      // Employment
      employmentType: employee.employementType || "N/A",
      skillLevel: employee.skillLevel || "N/A",
      probationPeriod: employee.probationPeriod || "N/A",
      employmentStatus: employee.employementStatus || "N/A",
      employmentLevel: employee.employmentLevel || "N/A",

      // Bank & Insurance
      aadharNumber: employee.aadharNumber || "N/A",
      insuranceNo: employee.insuranceNo || "N/A",
      panNumber: employee.panNumber || "N/A",
      pfNumber: employee.pfNumber || "N/A",
      uanNumber: employee.uanNumber || "N/A",
      bankName: employee.bankName || "N/A",
      accountHolderName: employee.accountHolderName || "N/A",
      accountNumber: employee.accountNumber || "N/A",
      ifscCode: employee.ifscCode || "N/A",
      bankAccountType: formatAccountType(employee.bankAccountType),
      upiId: employee.upiId || "N/A",

      // Authorities
      authorities:
        employee.authorities && employee.authorities.length > 0 ? (
          <div className="authorities-grid">
            {employee.authorities.map((a, index) => (
              <span key={index} className="authority-badge">
                {a.name}
              </span>
            ))}
          </div>
        ) : (
          "N/A"
        ),

      // Actions
      actions: (
        <div className="flex space-x-1">
          <IconButton
            icon="iconamoon:edit-light"
            variant="edit"
            onClick={() => handleEditUser(employee)}
            title="Edit Employee"
          />
          <IconButton
            icon="fluent:delete-28-regular"
            variant="delete"
            onClick={() => handleDeleteUser(employee.empId)}
            title="Delete Employee"
          />
        </div>
      ),
    };
  });

  function copyToClipboard(password) {
    if (!password) {
      toast.error("No password to copy!");
      return;
    }
    navigator.clipboard
      .writeText(password)
      .then(() => {
        toast.success("Password copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          rolesRes,
          branchesRes,
          employeesRes,
          authoritiesRes,
          managerRes,
          workingDayRes,
          accountantsRes,
          hrRes,
        ] = await Promise.all([
          axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/roles/enabled`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/branches/enabled/list-with-departments`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/employees/All`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/authorities/all`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/manager/list`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_BASE_URL}/workingdays`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/accountant/list`,
          ),
          axiosInstance.get(
            `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/hr/list`,
          ),
        ]);

        const rolesData = rolesRes.data.data;
        const branchesData = Array.isArray(branchesRes.data.data)
          ? branchesRes.data.data
          : [];
        const employeesData = employeesRes.data.data;
        const managersData = managerRes.data.data;
        const workingDaysData = workingDayRes.data.data || [];
        const accountantsData = accountantsRes.data.data.map((accountant) => ({
          value: accountant.empId,
          label: `${accountant.firstName} ${accountant.lastName}`,
        }));

        const hrData = hrRes.data.data.map((hr) => ({
          value: hr.empId,
          label: `${hr.firstName} ${hr.lastName}`,
        }));

        setAccountants(accountantsData || []);
        setHrPersons(hrData || []);

        setWorkingDays(workingDaysData);
        setRoles(rolesData);
        setBranches(branchesData);
        setEmployees(employeesData);
        setAuthorities(authoritiesRes.data.data);

        const formattedData = managersData.map((manager) => ({
          value: manager.empId,
          label: `${manager.firstName} ${manager.lastName}`,
        }));

        setManagers(formattedData || []);

        const authoritiesData = authoritiesRes.data.data.map((auth) => ({
          value: auth.id,
          label: auth.name,
        }));
        setAuthorities(authoritiesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleBranchChange = (branchId, isEditMode = false) => {
    const selectedId = parseInt(branchId);
    const selectedBranch = branches.find(
      (branch) => branch.branchId === selectedId,
    );

    if (selectedBranch) {
      // Set departments from selected branch
      setDepartments(selectedBranch.departments || []);

      // Set office timings from selected branch
      setFilteredOfficeTimings(selectedBranch.officeTimings || []);

      // For edit mode, show all working days for the branch
      // For create mode, apply department filtering
      if (isEditMode) {
        const branchWorkingDays = workingDays.filter((workingDay) =>
          workingDay.branches?.some((branch) => branch.branchId === selectedId),
        );
        setFilteredWorkingDays(branchWorkingDays);
      } else {
        // Filter working days based on selected branch for create mode
        const branchWorkingDays = workingDays.filter((workingDay) =>
          workingDay.branches?.some((branch) => branch.branchId === selectedId),
        );
        setFilteredWorkingDays(branchWorkingDays);
      }
    } else {
      setDepartments([]);
      setFilteredOfficeTimings([]);
      setFilteredWorkingDays([]);
    }
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);

    if (
      !values.password &&
      values.firstName &&
      values.lastName &&
      values.dob &&
      !selectedUser
    ) {
      values.password = generatePassword(
        values.firstName,
        values.lastName,
        values.dob,
      );
    }

    const normalizeValue = (value) =>
      value && value.trim() !== "" ? value : null;

    const employeeDetails = {
      empId: selectedUser ? selectedUser.empId : 0,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.personalEmail,
      companyEmail: values.officialEmail,
      mobileNo: values.mobile,
      employeeUserId: values.employeeUserId, // ADD THIS LINE HERE
      aadharNumber: normalizeValue(values.aadharNumber),
      emergencyContactName: values.emergencyContactName,
      emergencyContactNo: values.emergencyContactNo,
      dob: values.dob,
      doj: values.dateOfJoin,
      bloodGroup: values.bloodGroup,
      gender: values.gender,
      martialStatus: values.maritalStatus,
      employementType: values.employmentType || "PERMANENT",
      status: "ENABLED",
      managerId: values.managerId || 0,
      hrId: values.hrId || 0,
      accountantId: values.accountantId || 0,
      roleId: parseInt(values.roleId) || 0,
      branchId: parseInt(values.branchId) || 0,
      companyId: process.env.REACT_APP_COMPANY_ID,
      officeTimingId: values.officeTimingId || 0,
      workingDaysId: values.workingDayId,
      departmentIds: values.departmentIds,
      authorityIds: values.authorities.map((option) => option.value),
      skillLevel: values.skillLevel,
      probationPeriod: values.probationPeriod,
      employementStatus: values.employementStatus,
      employmentLevel: values.employmentLevel,
      pfNumber: normalizeValue(values.pfNumber),
      uanNumber: normalizeValue(values.uanNumber),
      insuranceNo: normalizeValue(values.insuranceNo),
      panNumber: normalizeValue(values.panNumber),
      bankName: normalizeValue(values.bankName),
      accountHolderName: normalizeValue(values.accountHolderName),
      accountNumber: normalizeValue(values.accountNumber),
      ifscCode: normalizeValue(values.ifscCode),
      branchName: normalizeValue(values.branchName),
      branchAddress: normalizeValue(values.branchAddress),
      bankAccountType: normalizeValue(values.bankAccountType),
      upiId: normalizeValue(values.upiId),
      micrCode: normalizeValue(values.micrCode),
      isPrimary: true,
    };

    if (!selectedUser) {
      employeeDetails.password = values.password;
    }

    const formData = new FormData();
    formData.append(
      "EmployeeDetails",
      new Blob([JSON.stringify(employeeDetails)], { type: "application/json" }),
    );
    formData.append(
      "ProfileImage",
      values.profilePic || new Blob([""], { type: "application/octet-stream" }),
    );

    try {
      if (selectedUser) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/employees`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.success("User updated successfully!");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/employees`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.success("User created successfully!");
      }

      handleModalClose();
      fetchEmployees();
    } catch (error) {
      console.error(error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowAddUserModal(false);
    setSelectedUser(null);
    setIsFormReady(false);
    setDepartments([]);
    setFilteredOfficeTimings([]);
    setFilteredWorkingDays([]);
    setMessage("");
  };

  const handleAddNewUser = () => {
    setSelectedUser(null);
    setDepartments([]);
    setFilteredOfficeTimings([]);
    setFilteredWorkingDays([]);
    setMessage("");
    setIsFormReady(true); // Set ready immediately for new user
    setShowAddUserModal(true);
  };

  const departmentOptions = departments.map((dept) => ({
    label: dept.departmentName,
    value: dept.departmentId, // number
  }));

  const officeTimingOptions = filteredOfficeTimings.map((timing) => ({
    label: `${timing.shiftName} (${timing.shiftForm} - ${timing.shiftTo})`,
    value: timing.officeTimingId,
  }));

  const workingDayOptions = filteredWorkingDays.map((day) => ({
    label: day.workingTypeName,
    value: day.workingDayId,
  }));

  const mapApiToFormValue = (apiValue, optionsArray) => {
    if (!apiValue || !optionsArray || optionsArray.length === 0) return "";

    // Try exact match first
    const exactMatch = optionsArray.find((option) => option.value === apiValue);
    if (exactMatch) return apiValue;

    // Try case-insensitive match
    const caseInsensitiveMatch = optionsArray.find(
      (option) => option.value.toLowerCase() === apiValue.toLowerCase(),
    );
    if (caseInsensitiveMatch) return caseInsensitiveMatch.value;

    // Try label match (for cases like "A Positive" -> "A_POSITIVE")
    const labelMatch = optionsArray.find(
      (option) => option.label.toLowerCase() === apiValue.toLowerCase(),
    );
    if (labelMatch) return labelMatch.value;

    // Try partial match for blood group (e.g., "A Positive" -> "A_POSITIVE")
    if (apiValue.includes(" ")) {
      const transformedValue = apiValue.replace(" ", "_").toUpperCase();
      const transformedMatch = optionsArray.find(
        (option) => option.value === transformedValue,
      );
      if (transformedMatch) return transformedMatch.value;
    }

    // Try to find by label with space normalization (e.g., "Two Months" -> "2_MONTHS")
    const normalizedLabelMatch = optionsArray.find((option) => {
      const normalizedLabel = option.label
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
      const normalizedApiValue = apiValue
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
      return normalizedLabel === normalizedApiValue;
    });
    if (normalizedLabelMatch) return normalizedLabelMatch.value;

    // Try to find by converting numbers to words (e.g., "Two Months" -> "2_MONTHS")
    const numberWordMap = {
      one: "1",
      two: "2",
      three: "3",
      four: "4",
      five: "5",
      six: "6",
      seven: "7",
      eight: "8",
      nine: "9",
      ten: "10",
      eleven: "11",
      twelve: "12",
    };

    let transformedApiValue = apiValue.toLowerCase();
    Object.entries(numberWordMap).forEach(([word, number]) => {
      transformedApiValue = transformedApiValue.replace(
        new RegExp(`\\b${word}\\b`, "g"),
        number,
      );
    });

    const numberWordMatch = optionsArray.find((option) => {
      const optionValue = option.value.toLowerCase().replace(/_/g, " ");
      return optionValue === transformedApiValue;
    });
    if (numberWordMatch) return numberWordMatch.value;

    // Debug logging to help identify mismatches
    console.warn(`No match found for API value: "${apiValue}"`);
    console.warn(
      "Available options:",
      optionsArray.map((opt) => ({ value: opt.value, label: opt.label })),
    );

    return "";
  };

  const getInitialValues = () => {
    const today = new Date().toISOString().split("T")[0];

    if (selectedUser) {
      return {
        employeeUserId: selectedUser.userEmployeeId || "",
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        dob: selectedUser.dob || "",
        password: "",
        aadharNumber: selectedUser.aadharNumber || "",
        dateOfJoin: selectedUser.doj || today,
        officialEmail: selectedUser.companyEmail || "",
        personalEmail: selectedUser.email || "",
        mobile: selectedUser.mobileNo?.toString() || "",
        hrId: selectedUser.hrId?.toString() || "",
        maritalStatus: mapApiToFormValue(
          selectedUser.martialStatus,
          maritalStatusOptions,
        ),
        gender: mapApiToFormValue(selectedUser.gender, genderOptions),
        bloodGroup: mapApiToFormValue(
          selectedUser.bloodGroup,
          bloodGroupOptions,
        ),
        emergencyContactName: selectedUser.emergencyContactName || "",
        emergencyContactNo: selectedUser.emergencyContactNo?.toString() || "",
        roleId: selectedUser.role?.roleId?.toString() || "",
        managerId: selectedUser.managerId?.toString() || "",
        accountantId: selectedUser.accountantId?.toString() || "",

        branchId: selectedUser.branch?.branchId?.toString() || "",
        departmentIds:
          selectedUser.departments?.map((dept) =>
            dept.departmentId.toString(),
          ) || [],
        officeTimingId: selectedUser.officeTimingId?.toString() || "",
        profilePic: null,
        authorities:
          selectedUser.authorities?.map((auth) => ({
            value: auth.id,
            label: auth.name,
          })) || [],
        workingDayId: selectedUser.workingDaysId?.toString() || "",
        pfNumber: selectedUser.pfNumber || "",
        uanNumber: selectedUser.uanNumber || "",
        skillLevel: mapApiToFormValue(
          selectedUser.skillLevel,
          skillLevelOptions,
        ),
        probationPeriod: mapApiToFormValue(
          selectedUser.probationPeriod,
          probationPeriodOptions,
        ),
        employementStatus: mapApiToFormValue(
          selectedUser.employementStatus,
          employmentStatusOptions,
        ),
        employmentLevel: mapApiToFormValue(
          selectedUser.employmentLevel,
          employmentLevelOptions,
        ),
        employmentType: mapApiToFormValue(
          selectedUser.employementType,
          employmentTypeOptions,
        ),
        insuranceNo: selectedUser.insuranceNo || "",
        panNumber: selectedUser.panNumber || "",
        bankName: selectedUser.bankName || "",
        accountHolderName: selectedUser.accountHolderName || "",
        accountNumber: selectedUser.accountNumber || "",
        ifscCode: selectedUser.ifscCode || "",
        branchName: selectedUser.branchName || "",
        branchAddress: selectedUser.branchAddress || "",

        // Apply mapApiToFormValue to bank account type as well
        bankAccountType: mapApiToFormValue(
          selectedUser.bankAccountType,
          bankAccountTypeOptions,
        ),

        upiId: selectedUser.upiId || "",
        micrCode: selectedUser.micrCode || "",
      };
    }

    return {
      firstName: "",
      lastName: "",
      aadharNumber: "",
      employeeUserId: "",
      dob: "",
      password: "",
      dateOfJoin: today,
      officialEmail: "",
      personalEmail: "",
      mobile: "",
      hrId: "",
      maritalStatus: "",
      gender: "",
      pfNumber: "",
      bloodGroup: "",
      emergencyContactName: "",
      emergencyContactNo: "",
      roleId: "",
      managerId: "",
      accountantId: "",
      branchId: "",
      departmentIds: [],
      profilePic: null,
      authorities: [],
      officeTimingId: "",
      workingDayId: "",
      skillLevel: "",
      uanNumber: "",
      probationPeriod: "",
      employementStatus: "",
      employmentLevel: "",
      employmentType: "",
      insuranceNo: "",
      panNumber: "",
      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      ifscCode: "",
      branchName: "",
      branchAddress: "",
      bankAccountType: "",
      upiId: "",
      micrCode: "",
    };
  };

  const downloadTemplate = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/employee-bulk/template`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "employee_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Template downloaded successfully!");
    } catch (error) {
      console.error("Error downloading template:", error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/employee-bulk/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200) {
        setUploadSuccess(true);
        setTemplateFile?.(selectedFile); // Optional: only if used

        toast.success("File uploaded successfully!");

        setTimeout(() => {
          setUploadSuccess(false);
          setShowUploadModal?.(false); // Optional: if using modal
          setSelectedFile(null);
          window.location.reload(); // Optional: if needed to refresh list
        }, 3000);
      } else {
        console.error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadSuccess(false);
  };

  const downloadExistingTemplate = () => {
    if (templateFile) {
      const url = window.URL.createObjectURL(templateFile);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", templateFile.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="user-management-container">
      <h1 className="display6">User Management</h1>

      <div className="user-management-actions">
        <ButtonWithIcon
          onClick={handleAddNewUser}
          className="add-button"
          text="Add new user"
          icon={"material-symbols-light:add"}
        >
          Add New User
        </ButtonWithIcon>

        <ButtonWithIcon
          className="bulk-upload-button"
          onClick={() => setShowUploadModal(true)}
          icon={"fas fa-upload"}
          text="Bulk upload users"
        />
      </div>

      {showAddUserModal && (
        <div className="user-modal-overlay">
          <div className="user-modal">
            <div className="user-modal-content">
              {message && (
                <div className="error-message-global">
                  <p>{message}</p>
                </div>
              )}
              <div className="modal-header">
                <div>
                  <h1 className="modal-title">
                    {selectedUser ? "Edit User" : "Add New User"}
                  </h1>
                  <p className="text-lg text-gray-600 mt-2">
                    <span className="text-red-500 font-bold">*</span> indicates
                    mandatory fields
                  </p>
                </div>

                {selectedUser && selectedUser.profileImage && (
                  <div className="header-profile-pic">
                    <img
                      src={`data:${
                        selectedUser.profileImageType || "image/jpeg"
                      };base64,${selectedUser.profileImage}`}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover border border-gray-300 shadow-sm"
                    />
                  </div>
                )}
              </div>

              <button
                className="close-button "
                onClick={() => {
                  handleBranchChange(0, false);
                  setShowAddUserModal(false);
                  setSelectedUser(null);
                }}
              >
                ×
              </button>

              <Formik
                key={`${selectedUser?.empId || "new"}-${isFormReady}`}
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                context={{ isEditMode: !!selectedUser }}
                onSubmit={handleFormSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  setFieldTouched,
                  isSubmitting,
                  isValid,
                }) => (
                  <Form className="modal-form">
                    <ScrollToError />

                    {/* Personal Information Section */}
                    <div className="form-section personal-info">
                      <h3 className="section-title">Personal Information</h3>
                      <div className="form-grid">
                        <div className="form-field">
                          <InputField
                            label="First Name *"
                            name="firstName"
                            required={true}
                            onBlur={() => setFieldTouched("firstName")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Last Name *"
                            name="lastName"
                            onBlur={() => setFieldTouched("lastName")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Date of Birth *"
                            name="dob"
                            type="date"
                            onBlur={() => setFieldTouched("dob")}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="form-section contact-info">
                      <h3 className="section-title">Contact Information</h3>
                      <div className="form-grid">
                        <div className="form-field">
                          <InputField
                            label="Official Email *"
                            name="officialEmail"
                            type="email"
                            onBlur={() => setFieldTouched("officialEmail")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Personal Email *"
                            name="personalEmail"
                            type="email"
                            onBlur={() => setFieldTouched("personalEmail")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Mobile Number *"
                            name="mobile"
                            type="text"
                            onBlur={() => setFieldTouched("mobile")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Emergency Contact Name *"
                            name="emergencyContactName"
                            onBlur={() =>
                              setFieldTouched("emergencyContactName")
                            }
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Emergency Contact No *"
                            name="emergencyContactNo"
                            onBlur={() => setFieldTouched("emergencyContactNo")}
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="profilePic">Profile Picture</label>
                          <div id="profilePic" tabIndex={-1}>
                            <FileInput
                              id="profilePic"
                              allowedTypes={[
                                "image/jpeg",
                                "image/png",
                                "image/jpg",
                              ]}
                              maxSize={500 * 1024}
                              errorMessageSize="File size must be 500KB or less."
                              errorMessageType="Only JPG, JPEG, PNG files are allowed."
                              onFileSelect={(file) =>
                                setFieldValue("profilePic", file)
                              }
                              onChange={() =>
                                setFieldTouched("profilePic", true)
                              }
                            />
                          </div>

                          {/* Show selected new file preview */}
                          {values.profilePic && (
                            <div className="new-profile-pic-display">
                              <img
                                src={URL.createObjectURL(values.profilePic)}
                                alt="New Profile Preview"
                                className="profile-pic-preview"
                              />
                              <p className="profile-pic-label">
                                New Profile Picture (Preview)
                              </p>
                            </div>
                          )}

                          {errors.profilePic && touched.profilePic && (
                            <div className="error-message">
                              {errors.profilePic}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Professional Information Section */}
                    <div className="form-section professional-info">
                      <h3 className="section-title">
                        Professional Information
                      </h3>
                      <div className="form-grid">
                        {/* Employee User ID - Always visible */}
                        <div className="form-field">
                          <InputField
                            label="Employee User ID"
                            name="employeeUserId"
                            onBlur={() => setFieldTouched("employeeUserId")}
                            placeholder="Enter company ID or leave it blank to auto generate."
                          />
                        </div>
                        {/* Password Field */}
                        {!selectedUser && (
                          <div className="w-full mb-4">
                            {/* Label */}
                            <label
                              htmlFor="password"
                              className="text-sm font-semibold text-gray-700 mb-[13px] block"
                            >
                              Password <span className="text-red-500">*</span>
                            </label>

                            {/* Input + Icons Container */}
                            <div className="flex items-center border border-gray-300 rounded-xl p-[10px] bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                              {/* Input Field */}
                              <input
                                id="password"
                                name="password"
                                type="text"
                                value={values.password}
                                onChange={(e) => {
                                  setFieldValue("password", e.target.value);
                                }}
                                onBlur={() => setFieldTouched("password")}
                                placeholder={
                                  values.firstName &&
                                  values.lastName &&
                                  values.dob
                                    ? generatePassword(
                                        values.firstName,
                                        values.lastName,
                                        values.dob,
                                      )
                                    : "Enter Name and DOB to see suggested password"
                                }
                                className="flex-1 text-gray-700 placeholder-gray-400 focus:outline-none text-base font-medium"
                              />

                              {/* Action Icons */}
                              <div className="flex items-center space-x-3 ml-2">
                                {/* Generate Password Button */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (
                                      values.firstName &&
                                      values.lastName &&
                                      values.dob
                                    ) {
                                      const password = generatePassword(
                                        values.firstName,
                                        values.dob,
                                      );
                                      setFieldValue("password", password);
                                    } else {
                                      toast.warning(
                                        "Please fill in First Name and Date of Birth first",
                                      );
                                    }
                                  }}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Generate Password"
                                >
                                  <i className="fas fa-key text-lg"></i>
                                </button>

                                {/* Copy Password */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    copyToClipboard(values.password)
                                  }
                                  className="text-green-600 hover:text-green-800"
                                  title="Copy Password"
                                  disabled={!values.password}
                                >
                                  <i className="fas fa-copy text-lg"></i>
                                </button>

                                {/* Info Icon */}
                                <span
                                  className="cursor-pointer"
                                  data-tooltip-id="passwordTooltip"
                                  data-tooltip-content="Click the key icon to generate password. Format: First 3 letters of First Name + DDMM of DOB."
                                >
                                  <i className="fa-solid fa-circle-info text-gray-500 text-lg"></i>
                                </span>
                              </div>
                            </div>

                            {/* Error */}
                            {errors.password && touched.password && (
                              <div className="text-red-600 text-xs mt-1">
                                {errors.password}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="form-field">
                          <InputField
                            label="Aadhar Card Number"
                            name="aadharNumber"
                            onBlur={() => setFieldTouched("aadharNumber")}
                          />
                        </div>

                        <div className="form-field">
                          <InputField
                            label="Date of Joining *"
                            name="dateOfJoin"
                            type="date"
                            onBlur={() => setFieldTouched("dateOfJoin")}
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="employmentType">
                            Employment Type *
                          </label>
                          <ButtonGroup
                            options={employmentTypeOptions}
                            value={values.employmentType}
                            onChange={(selectedValue) => {
                              setFieldValue("employmentType", selectedValue);
                            }}
                            name="employmentType"
                            error={errors.employmentType}
                            touched={touched.employmentType}
                          />
                        </div>

                        {/* Branch Selection */}
                        <div className="form-field">
                          <label htmlFor="branchId">Branch *</label>
                          <select
                            id="branchId"
                            name="branchId"
                            value={values.branchId}
                            onChange={(e) => {
                              const selectedBranchId = e.target.value;
                              setFieldValue("branchId", selectedBranchId);

                              // Only reset fields for create mode
                              if (!selectedUser) {
                                setFieldValue("departmentIds", []);
                                setFieldValue("officeTimingId", "");
                                setFieldValue("workingDayId", "");
                              }

                              handleBranchChange(
                                selectedBranchId,
                                !!selectedUser,
                              );
                            }}
                            onBlur={() => setFieldTouched("branchId")}
                          >
                            <option value="">Select Branch</option>
                            {branches.map((branch) => (
                              <option
                                key={branch.branchId}
                                value={branch.branchId}
                              >
                                {branch.branchName}
                              </option>
                            ))}
                          </select>
                          {errors.branchId && touched.branchId && (
                            <div className="error-message">
                              {errors.branchId}
                            </div>
                          )}
                        </div>

                        {/* Role Selection */}
                        <div className="form-field">
                          <label htmlFor="roleId">Role *</label>
                          <select
                            id="roleId"
                            name="roleId"
                            value={values.roleId}
                            onChange={(e) =>
                              setFieldValue("roleId", e.target.value)
                            }
                            onBlur={() => setFieldTouched("roleId")}
                          >
                            <option value="">Select Role</option>
                            {roles.map((role) => (
                              <option key={role.roleId} value={role.roleId}>
                                {role.roleName}
                              </option>
                            ))}
                          </select>
                          {errors.roleId && touched.roleId && (
                            <div className="error-message">{errors.roleId}</div>
                          )}
                        </div>

                        {/* Manager Selection */}
                        <div className="form-field">
                          <label htmlFor="managerId">Manager</label>
                          <div
                            className="select-container"
                            id="managerId"
                            tabIndex={-1}
                          >
                            <Select
                              id="managerId-select"
                              name="managerId"
                              options={managers}
                              value={
                                managers.find(
                                  (manager) =>
                                    manager.value ===
                                    parseInt(values.managerId),
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "managerId",
                                  selectedOption ? selectedOption.value : "",
                                )
                              }
                              placeholder="Select a Manager"
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          {errors.managerId && touched.managerId && (
                            <div className="error-message">
                              {errors.managerId}
                            </div>
                          )}
                        </div>

                        <div className="form-field">
                          <label htmlFor="accountantId">Accountant</label>
                          <div
                            className="select-container"
                            id="accountantId"
                            tabIndex={-1}
                          >
                            <Select
                              id="accountantId-select"
                              name="accountantId"
                              options={accountants}
                              value={
                                accountants.find(
                                  (accountant) =>
                                    accountant.value ===
                                    parseInt(values.accountantId),
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "accountantId",
                                  selectedOption ? selectedOption.value : "",
                                )
                              }
                              placeholder="Select an Accountant"
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          {errors.accountantId && touched.accountantId && (
                            <div className="error-message">
                              {errors.accountantId}
                            </div>
                          )}
                        </div>
                        <div className="form-field">
                          <label htmlFor="hrId">HR</label>
                          <div
                            className="select-container"
                            id="hrId"
                            tabIndex={-1}
                          >
                            <Select
                              id="hrId-select"
                              name="hrId"
                              options={hrPersons}
                              value={
                                hrPersons.find(
                                  (hr) => hr.value === parseInt(values.hrId),
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "hrId",
                                  selectedOption ? selectedOption.value : "",
                                )
                              }
                              placeholder="Select an HR"
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          {errors.hrId && touched.hrId && (
                            <div className="error-message">{errors.hrId}</div>
                          )}
                        </div>

                        <div className="form-field">
                          <label htmlFor="maritalStatus">
                            Marital Status *
                          </label>
                          <ButtonGroup
                            options={maritalStatusOptions}
                            value={values.maritalStatus}
                            onChange={(selectedValue) => {
                              setFieldValue("maritalStatus", selectedValue);
                            }}
                            name="maritalStatus"
                            error={errors.maritalStatus}
                            touched={touched.maritalStatus}
                          />
                        </div>

                        <div className="form-field">
                          <label htmlFor="gender">Gender *</label>
                          <ButtonGroup
                            options={genderOptions}
                            value={values.gender}
                            onChange={(selectedValue) => {
                              setFieldValue("gender", selectedValue);
                            }}
                            name="gender"
                            error={errors.gender}
                            touched={touched.gender}
                          />
                        </div>

                        <div className="form-field">
                          <label htmlFor="bloodGroup">Blood Group *</label>
                          <ButtonGroup
                            options={bloodGroupOptions}
                            value={values.bloodGroup}
                            onChange={(selectedValue) => {
                              setFieldValue("bloodGroup", selectedValue);
                            }}
                            name="bloodGroup"
                            error={errors.bloodGroup}
                            touched={touched.bloodGroup}
                          />
                        </div>
                        {/* Add after the existing professional fields */}
                        <div className="form-field">
                          <label htmlFor="skillLevel">Skill Level *</label>
                          <ButtonGroup
                            options={skillLevelOptions}
                            value={values.skillLevel}
                            onChange={(selectedValue) => {
                              setFieldValue("skillLevel", selectedValue);
                            }}
                            name="skillLevel"
                            error={errors.skillLevel}
                            touched={touched.skillLevel}
                          />
                        </div>

                        <div className="form-field">
                          <label htmlFor="probationPeriod">
                            Probation Period *
                          </label>
                          <ButtonGroup
                            options={probationPeriodOptions}
                            value={values.probationPeriod}
                            onChange={(selectedValue) => {
                              setFieldValue("probationPeriod", selectedValue);
                            }}
                            name="probationPeriod"
                            error={errors.probationPeriod}
                            touched={touched.probationPeriod}
                          />
                        </div>

                        <div className="form-field">
                          <label htmlFor="employementStatus">
                            Employment Status *
                          </label>
                          <ButtonGroup
                            options={employmentStatusOptions}
                            value={values.employementStatus}
                            onChange={(selectedValue) => {
                              setFieldValue("employementStatus", selectedValue);
                            }}
                            name="employementStatus"
                            error={errors.employementStatus}
                            touched={touched.employementStatus}
                          />
                        </div>

                        <div className="form-field">
                          <label htmlFor="employmentLevel">
                            Employment Level *
                          </label>
                          <ButtonGroup
                            options={employmentLevelOptions}
                            value={values.employmentLevel}
                            onChange={(selectedValue) => {
                              setFieldValue("employmentLevel", selectedValue);
                            }}
                            name="employmentLevel"
                            error={errors.employmentLevel}
                            touched={touched.employmentLevel}
                          />
                        </div>

                        <div className="form-field">
                          <InputField
                            label="Insurance Number"
                            name="insuranceNo"
                            onBlur={() => setFieldTouched("insuranceNo")}
                          />
                        </div>

                        <div className="form-field">
                          <InputField
                            label="PAN Number"
                            name="panNumber"
                            onBlur={() => setFieldTouched("panNumber")}
                          />
                        </div>

                        <div className="form-field">
                          <InputField
                            label="PF Number"
                            name="pfNumber"
                            onBlur={() => setFieldTouched("pfNumber")}
                          />
                        </div>

                        <div className="form-field">
                          <InputField
                            label="UAN Number"
                            name="uanNumber"
                            onBlur={() => setFieldTouched("uanNumber")}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Work Schedule & Permissions */}
                    <div className="form-section work-schedule">
                      <h3 className="section-title">
                        Work Schedule & Permissions
                      </h3>
                      <div className="form-grid">
                        {/* Department Selection */}
                        <div className="form-field">
                          <EnhancedMultiSelect
                            label="Departments *"
                            options={departmentOptions}
                            value={departmentOptions.filter((opt) =>
                              Array.isArray(values.departmentIds)
                                ? values.departmentIds
                                    .map(String)
                                    .includes(opt.value.toString())
                                : false,
                            )}
                            onChange={(selectedOptions) => {
                              const departmentIds = selectedOptions.map(
                                (option) => option.value,
                              );
                              setFieldValue("departmentIds", departmentIds);
                            }}
                            placeholder="Select Departments"
                            name="departmentIds"
                            error={errors.departmentIds}
                            touched={touched.departmentIds}
                            dependencyMessage={
                              !selectedUser &&
                              values.branchId &&
                              departmentOptions.length === 0
                                ? "No departments available for the selected branch. Please add departments to this branch first."
                                : null
                            }
                          />
                          {departmentOptions.length === 0 && (
                            <p className="dependency-message">
                              No departments available for the selected branch.
                            </p>
                          )}
                        </div>

                        {/* Authorities Selection */}

                        <div className="form-field">
                          <EnhancedMultiSelect
                            label="Access *"
                            options={authorities.map((auth) => ({
                              value: auth.value,
                              label: auth.label,
                            }))}
                            value={values.authorities || []}
                            onChange={(selectedOptions) =>
                              setFieldValue("authorities", selectedOptions)
                            }
                            placeholder="Select Access"
                            name="authorities"
                            error={errors.authorities}
                            touched={touched.authorities}
                          />
                        </div>

                        {/* Office Timings Selection */}
                        <div className="form-field">
                          <label htmlFor="officeTimingId">
                            Office Timing *
                          </label>
                          <div
                            className="select-container"
                            id="officeTimingId"
                            tabIndex={-1}
                          >
                            <Select
                              id="officeTimingId-select"
                              name="officeTimingId"
                              options={officeTimingOptions}
                              value={
                                officeTimingOptions.find(
                                  (option) =>
                                    option.value.toString() ===
                                    values.officeTimingId?.toString(),
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "officeTimingId",
                                  selectedOption ? selectedOption.value : "",
                                )
                              }
                              placeholder="Select Office Timing"
                              classNamePrefix="react-select"
                              isDisabled={officeTimingOptions.length === 0}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          {/* Only show dependency message for new users */}
                          {!selectedUser &&
                            values.branchId &&
                            officeTimingOptions.length === 0 && (
                              <div className="dependency-message">
                                <i className="fas fa-info-circle"></i>
                                No office timings available for the selected
                                branch. Please add office timings to this branch
                                first.
                              </div>
                            )}
                          {errors.officeTimingId && touched.officeTimingId && (
                            <div className="error-message">
                              {errors.officeTimingId}
                            </div>
                          )}
                        </div>

                        {/* Working Days */}
                        <div className="form-field">
                          <label htmlFor="workingDays">Working Days *</label>
                          <div
                            className="select-container"
                            id="workingDayId"
                            tabIndex={-1}
                          >
                            <Select
                              id="workingDayId-select"
                              name="workingDayId"
                              options={workingDayOptions}
                              value={
                                workingDayOptions?.find(
                                  (option) =>
                                    option.value.toString() ===
                                    values.workingDayId?.toString(),
                                ) || null
                              }
                              onChange={(selectedOption) =>
                                setFieldValue(
                                  "workingDayId",
                                  selectedOption ? selectedOption.value : "",
                                )
                              }
                              placeholder="Select Working Days"
                              classNamePrefix="react-select"
                              isDisabled={workingDayOptions.length === 0}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          {/* Only show dependency message for new users */}
                          {!selectedUser &&
                            (values.branchId ||
                              values.departmentIds?.length > 0) &&
                            workingDayOptions.length === 0 && (
                              <div className="dependency-message">
                                <i className="fas fa-info-circle"></i>
                                No working days available for the selected
                                branch and departments. Working days are
                                filtered based on your branch and department
                                selection. Please add working days configuration
                                for this combination.
                              </div>
                            )}

                          {errors.workingDayId && touched.workingDayId && (
                            <div className="error-message">
                              {errors.workingDayId}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Banking Information Section */}
                    <div className="form-section banking-info">
                      <h3 className="section-title">Banking Information</h3>
                      <div className="form-grid">
                        <div className="form-field">
                          <InputField
                            label="Bank Name"
                            name="bankName"
                            onBlur={() => setFieldTouched("bankName")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Account Holder Name"
                            name="accountHolderName"
                            onBlur={() => setFieldTouched("accountHolderName")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="Account Number"
                            name="accountNumber"
                            onBlur={() => setFieldTouched("accountNumber")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="IFSC Code"
                            name="ifscCode"
                            onBlur={() => setFieldTouched("ifscCode")}
                          />
                        </div>
                        <div className="form-field">
                          <InputField
                            label="UPI ID"
                            name="upiId"
                            onBlur={() => setFieldTouched("upiId")}
                          />
                        </div>
                        <div className="form-field">
                          <label htmlFor="bankAccountType">
                            Bank Account Type
                          </label>
                          <ButtonGroup
                            options={bankAccountTypeOptions}
                            value={values.bankAccountType}
                            onChange={(selectedValue) => {
                              setFieldValue("bankAccountType", selectedValue);
                            }}
                            name="bankAccountType"
                            error={errors.bankAccountType}
                            touched={touched.bankAccountType}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-actions">
                      <Tooltip
                        id="passwordTooltip"
                        place="top"
                        style={{ maxWidth: 220, textAlign: "center" }}
                      />

                      <ButtonWithIcon
                        type="submit"
                        className="submit-button"
                        text="Submit"
                        icon={"tdesign:send"}
                      />
                      <ButtonWithIcon
                        type="button"
                        onClick={() => {
                          handleBranchChange(0, false);
                          setShowAddUserModal(false);
                          setSelectedUser(null);
                        }}
                        className="cancel-button"
                        text="Cancel"
                        icon={"lets-icons:cancel-light"}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Upload CSV Template</h3>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {uploadSuccess ? (
                <div className="success-message">
                  <i className="fas fa-check-circle"></i>
                  File uploaded successfully!
                </div>
              ) : (
                <>
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download"></i> Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="master-file-upload"
                      className="file-upload-label"
                    >
                      <i className="fas fa-cloud-upload-alt"></i>
                      {selectedFile ? selectedFile.name : "Choose a CSV file"}
                    </label>
                    <input
                      id="master-file-upload"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>
                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    Upload
                  </button>

                  <button className="cancel-button" onClick={closeModal}>
                    Cancel
                  </button>
                </>
              )}
              {templateFile && (
                <div className="template-section">
                  <h4>Available Template</h4>
                  <div className="template-file">
                    <span>{templateFile.name}</span>
                    <button
                      className="download-button"
                      onClick={downloadExistingTemplate}
                    >
                      <i className="fas fa-download"></i> Download
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="user-table-container">
        <GeneralTable
          columns={horizontalTableColumns}
          data={horizontalTransformedData}
          title="Employee Management"
          initialPageSize={10}
          showPagination={true}
        />
      </div>
    </div>
  );
};

UserManagement.propTypes = {};

export default UserManagement;
